import type { SearchHit } from '@contracts';
import { Command } from 'cmdk';
import { Activity, ArrowRight, Building2, CornerDownLeft, History, Loader2, MapPin, Monitor, ScrollText, Search, Settings, Sparkles, TriangleAlert } from 'lucide-react';
import { Dialog } from 'radix-ui';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { applyInterpretation, interpret } from '../ask/interpret';
import { recordPath } from '../records/paths';
import { recordContext, useScope, useWorkspaces } from '../scope/scope';
import { useCatalog, useFacets, useSearch } from '../search/hooks';
import { emptyFilters, toSearchQuery, writeFilters } from '../search/state';
import { cn } from '../ui/cn';
import { Kbd } from '../ui/misc';
import { StatusBadge } from '../ui/status';
import { FilterSummary } from '../filters/FilterSummary';
import { DeviceIdentity } from '../devices/DeviceIdentity';

export function useDebounced<T>(value: T, ms: number) {
  const [current, setCurrent] = useState(value);
  useEffect(() => { const id = setTimeout(() => setCurrent(value), ms); return () => clearTimeout(id); }, [value, ms]);
  return current;
}

export const kindIcon = { organization: Building2, site: MapPin, device: Monitor } as const;

export function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim();
  const at = q ? text.toLowerCase().indexOf(q.toLowerCase()) : -1;
  if (at < 0) return <>{text}</>;
  return <>{text.slice(0, at)}<mark className="rounded-sm bg-brand-soft px-px text-ink">{text.slice(at, at + q.length)}</mark>{text.slice(at + q.length)}</>;
}

const SUGGESTIONS = ['What needs a look?', 'Devices that haven’t checked in this week', 'Which customers have offline devices?', 'Devices that never reported'];

export function CommandPalette({ open, initial, onOpenChange }: { open: boolean; initial: string; onOpenChange: (open: boolean) => void }) {
  const { mspId, mspName, coverage, recents, remember } = useScope();
  const workspaces = useWorkspaces();
  const navigate = useNavigate();
  const [text, setText] = useState(initial);
  useEffect(() => { if (open) setText(initial); }, [open, initial]);
  const term = useDebounced(text.trim(), 110);
  const { catalog, isSuccess: catalogLoaded, isError: catalogFailed, isFetching: catalogFetching, refetch: retryCatalog } = useCatalog();
  // Without customer and site names, “offline devices at Acme” would read as every offline device.
  const reading = useMemo(() => (term && catalogLoaded ? interpret(term, catalog) : null), [term, catalog, catalogLoaded]);
  const readQuery = reading?.understood ? toSearchQuery(applyInterpretation(emptyFilters, reading)) : null;
  const results = useSearch(open && term ? { version: 1, q: term, limit: 12 } : null);
  const readCount = useFacets(open ? readQuery : null);
  const hits = term ? results.data?.hits ?? [] : [];

  const go = (path: string) => { onOpenChange(false); navigate(path); };
  const openHit = (hit: SearchHit) => { remember(hit); go(recordPath(mspId, hit.kind, hit.id)); };
  const ask = (question: string) => go(`/w/${mspId}/ask?${new URLSearchParams({ q: question })}`);
  const applyReading = () => {
    if (!reading) return;
    const state = applyInterpretation(emptyFilters, reading);
    const params = writeFilters(state);
    params.set('from', reading.input);
    go(state.kind === 'device' ? `/w/${mspId}/devices?${params}` : `/w/${mspId}/search?${params}`);
  };
  const groups = (['device', 'organization', 'site'] as const)
    .map(kind => ({ kind, hits: hits.filter(h => h.kind === kind) })).filter(g => g.hits.length)
    .sort((a, b) => hits.indexOf(a.hits[0]!) - hits.indexOf(b.hits[0]!));
  const groupName = { device: 'Devices', organization: 'Customers', site: 'Sites' };
  // Keep the highlight on a row that exists as async results replace the list.
  const firstValue = !term ? (recents[0] ? `recent-${recents[0].id}` : `ask-${SUGGESTIONS[0]}`)
    : reading?.understood ? 'reading' : groups[0]?.hits[0]?.id ?? 'ask';
  const [active, setActive] = useState(firstValue);
  useEffect(() => setActive(firstValue), [firstValue]);
  const pending = !!term && (results.isFetching || term !== text.trim());
  const noMatches = !!term && results.isSuccess && !hits.length && !pending;
  const readingPending = !!term && !catalogLoaded && catalogFetching;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-[rgb(5_15_23/0.45)] animate-fade" />
        <Dialog.Content aria-describedby={undefined}
          className="fixed inset-x-0 top-0 z-50 mx-auto flex max-h-[100dvh] w-full flex-col overflow-hidden border-line bg-raised shadow-pop animate-pop sm:top-[12vh] sm:max-h-[72vh] sm:w-[min(680px,calc(100vw-2rem))] sm:rounded-2xl sm:border">
          <Dialog.Title className="sr-only">Search or ask</Dialog.Title>
          <Command shouldFilter={false} loop label="Search or ask" value={active} onValueChange={setActive} className="flex min-h-0 flex-1 flex-col">
            <div className="flex items-center gap-3 border-b border-line px-4">
              {pending || readingPending ? <Loader2 className="size-[18px] shrink-0 animate-spin text-ink-3" /> : <Search className="size-[18px] shrink-0 text-ink-3" />}
              <Command.Input value={text} onValueChange={setText} autoFocus
                placeholder="Hostname, short ID, serial, customer — or “offline devices at Acme”"
                className="h-14 min-w-0 flex-1 bg-transparent text-[15px] text-ink outline-none placeholder:text-ink-3" />
              <button type="button" onClick={() => onOpenChange(false)} className="sm:hidden text-[13px] font-medium text-ink-2">Cancel</button>
              <Kbd className="hidden sm:inline-flex">Esc</Kbd>
            </div>
            <Command.List className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-2 [&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:pt-2.5 [&_[cmdk-group-heading]]:text-[12px] [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-ink-3">
              {!term && (<>
                {recents.length > 0 && (
                  <Command.Group heading="Recent">
                    {recents.map(r => (
                      <Row key={r.id} value={`recent-${r.id}`} onSelect={() => go(recordPath(mspId, r.kind, r.id))}
                        icon={<History className="size-4" />} title={r.title} mono={r.kind === 'device'} detail={<>{r.context}{r.kind === 'device' && <DeviceIdentity device={r} />}</>}
                        aside={r.kind === 'device' ? <StatusBadge status={r.status} compact /> : null} />
                    ))}
                  </Command.Group>
                )}
                <Command.Group heading="Ask">
                  {SUGGESTIONS.map(s => <Row key={s} value={`ask-${s}`} onSelect={() => ask(s)} icon={<Sparkles className="size-4 text-brand" />} title={s} />)}
                </Command.Group>
                <Command.Group heading="Go to">
                  <Row value="go-today" onSelect={() => go(`/w/${mspId}/today`)} icon={<Activity className="size-4" />} title="Today" aside={<Shortcut keys={['G', 'T']} />} />
                  <Row value="go-devices" onSelect={() => go(`/w/${mspId}/devices`)} icon={<Monitor className="size-4" />} title="Devices" aside={<Shortcut keys={['G', 'D']} />} />
                  <Row value="go-customers" onSelect={() => go(`/w/${mspId}/customers`)} icon={<Building2 className="size-4" />} title="Customers" aside={<Shortcut keys={['G', 'C']} />} />
                  <Row value="go-audit" onSelect={() => go(`/w/${mspId}/audit`)} icon={<ScrollText className="size-4" />} title="Audit trail" />
                  <Row value="go-settings" onSelect={() => go(`/w/${mspId}/settings`)} icon={<Settings className="size-4" />} title="Settings and data sources" />
                </Command.Group>
                {workspaces.length > 1 && (
                  <Command.Group heading="Switch workspace">
                    {workspaces.filter(w => w.id !== mspId).map(w => (
                      <Row key={w.id} value={`ws-${w.id}`} onSelect={() => go(`/w/${w.id}/today`)} icon={<ArrowRight className="size-4" />} title={w.name} detail={w.coverage} />
                    ))}
                  </Command.Group>
                )}
              </>)}
              {term && reading?.understood && (
                <Command.Group heading="Filter">
                  <Row value="reading" onSelect={applyReading} icon={<Sparkles className="size-4 text-brand" />}
                    title={<FilterSummary filters={reading.filters} catalog={catalog} />}
                    aside={<span className="tabular text-[12px] text-ink-3">{readCount.data && !readCount.isPlaceholderData ? `${readCount.data.total} match${readCount.data.total === 1 ? '' : 'es'}` : '…'}</span>} />
                </Command.Group>
              )}
              {term && catalogFailed && !catalogFetching && (
                <Command.Group heading="Filter">
                  <Row value="catalog-retry" onSelect={() => void retryCatalog()} icon={<TriangleAlert className="size-4 text-attention" />}
                    title="Can’t read questions as filters right now" detail="Customer and site names didn’t load, so a filter could leave part of the question out. Select to try again." />
                </Command.Group>
              )}
              {term && results.isError && !pending && (
                <Command.Group heading="Search">
                  <Row value="search-retry" onSelect={() => void results.refetch()} icon={<TriangleAlert className="size-4 text-attention" />}
                    title="Search didn’t complete" detail="Records may exist that aren’t shown. Select to try again." />
                </Command.Group>
              )}
              {groups.map(g => (
                <Command.Group key={g.kind} heading={groupName[g.kind]}>
                  {g.hits.map(hit => {
                    const Icon = kindIcon[hit.kind];
                    return (
                      <Row key={hit.id} value={hit.id} onSelect={() => openHit(hit)} icon={<Icon className="size-4" />}
                        title={<Highlight text={hit.title} query={term} />} mono={hit.kind === 'device'} detail={<>{recordContext(hit)}{hit.kind === 'device' && <DeviceIdentity device={hit} />}</>}
                        aside={hit.kind === 'device' ? <StatusBadge status={hit.status} compact /> : null} />
                    );
                  })}
                </Command.Group>
              ))}
              {noMatches && !reading?.understood && <p className="px-3 py-6 text-center text-ink-3">No customers, sites or devices match “{term}”.</p>}
              {term && (
                <Command.Group heading="More">
                  <Row value="ask" onSelect={() => ask(text.trim())} icon={<Sparkles className="size-4 text-brand" />}
                    title={<span>Ask: <span className="text-ink">“{text.trim()}”</span></span>} detail="Read the question, gather evidence and brief me" />
                  {hits.length > 0 && (
                    <Row value="all" onSelect={() => go(`/w/${mspId}/search?${new URLSearchParams({ q: term })}`)} icon={<Search className="size-4" />}
                      title={`See all ${results.data?.total ?? ''} results`} />
                  )}
                </Command.Group>
              )}
            </Command.List>
            <div className="flex items-center gap-3 border-t border-line bg-sunken/50 px-4 py-2 text-[12px] text-ink-3">
              <span className="min-w-0 flex-1 truncate">Searching <span className="font-medium text-ink-2">{mspName}</span> · {coverage} · inventory records</span>
              <span className="hidden items-center gap-1.5 sm:flex"><Kbd>↑</Kbd><Kbd>↓</Kbd> move <Kbd><CornerDownLeft className="size-3" /></Kbd> open</span>
            </div>
          </Command>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Shortcut({ keys }: { keys: string[] }) {
  return <span className="hidden gap-1 sm:flex">{keys.map(k => <Kbd key={k}>{k}</Kbd>)}</span>;
}

function Row({ value, onSelect, icon, title, detail, aside, mono }: {
  value: string; onSelect: () => void; icon: ReactNode; title: ReactNode; detail?: ReactNode; aside?: ReactNode; mono?: boolean;
}) {
  return (
    <Command.Item value={value} onSelect={onSelect}
      className="group flex min-h-11 cursor-pointer items-center gap-3 rounded-lg px-2.5 py-1.5 text-ink-2 outline-none data-[selected=true]:bg-select data-[selected=true]:text-ink">
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-line bg-panel text-ink-3 group-data-[selected=true]:text-ink-2">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className={cn('block truncate font-medium text-ink', mono && 'font-mono text-[13px]')}>{title}</span>
        {detail && <span className="block truncate text-[12.5px] text-ink-3">{detail}</span>}
      </span>
      {aside}
    </Command.Item>
  );
}
